import { FC, useMemo } from "react";
import { Box } from "@mui/material";
import { ITrainControlsResponse } from "../../interfaces";

interface ITiSchemeLineProps {
  control: ITrainControlsResponse;
  flashUnacknowledgedAlarms: boolean;
  flashFaultAlarms: boolean;
}

const DEFAULT_STROKE = "#333333";
const ARROW_GDI_BASE_SCALE = 1.6;

const ARROW_GLOBAL_SCALE = 1.5;

function normalizeHex(raw: string): string {
  const t = raw.trim();
  return t.startsWith("#") ? t : `#${t}`;
}

function parseNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function normalizeLineSettingsUInt32Array(raw: unknown): number[] | null {
  if (raw == null) return null;
  const pushCoerced = (acc: number[], v: unknown): boolean => {
    if (typeof v === "number" && Number.isFinite(v)) {
      acc.push(v);
      return true;
    }
    if (typeof v === "string" && v.trim() !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) {
        acc.push(n);
        return true;
      }
    }
    return false;
  };
  if (Array.isArray(raw)) {
    const nums: number[] = [];
    for (const v of raw) {
      if (!pushCoerced(nums, v)) return null;
    }
    return nums.length > 0 ? nums : null;
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    const keys = Object.keys(o).filter(k => /^\d+$/.test(k));
    if (keys.length === 0) return null;
    const max = Math.max(...keys.map(k => Number(k)));
    const nums: number[] = [];
    for (let i = 0; i <= max; i++) {
      if (!pushCoerced(nums, o[String(i)])) return null;
    }
    return nums.length > 0 ? nums : null;
  }
  return null;
}

function gatherLineProps(p: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...p };
  const nestKeys = ["Line", "Shape", "Drawing", "Geometry", "Pen", "Stroke", "Border"];
  for (const nk of nestKeys) {
    const sub = p[nk];
    if (!sub || typeof sub !== "object" || Array.isArray(sub)) continue;
    const o = sub as Record<string, unknown>;
    for (const [k, v] of Object.entries(o)) {
      if (out[k] === undefined || out[k] === null) out[k] = v;
    }
  }
  const line = p.Line;
  if (line && typeof line === "object" && !Array.isArray(line)) {
    const lo = line as Record<string, unknown>;
    if (!out.StartPoint && lo.StartPoint) out.StartPoint = lo.StartPoint;
    if (!out.EndPoint && lo.EndPoint) out.EndPoint = lo.EndPoint;
    if (!out.StartPoint && lo.Point1) out.StartPoint = lo.Point1;
    if (!out.EndPoint && lo.Point2) out.EndPoint = lo.Point2;
    if (out.Direction == null && lo.Direction != null) out.Direction = lo.Direction;
    if (out.Angle == null && lo.Angle != null) out.Angle = lo.Angle;
  }
  if (out.Direction == null && out.LineDirection != null) out.Direction = out.LineDirection;
  const u32 = normalizeLineSettingsUInt32Array(out.LineSettingsRawUInt32);
  if (u32) out.LineSettingsRawUInt32 = u32;
  return out;
}

function readPoint(obj: Record<string, unknown> | null): { x: number | null; y: number | null } {
  if (!obj) return { x: null, y: null };
  const x = parseNumber(obj.X ?? obj.x ?? obj.Left ?? obj.left);
  const y = parseNumber(obj.Y ?? obj.y ?? obj.Top ?? obj.top);
  return { x, y };
}

function endpointsFromExplicitProps(
  p: Record<string, unknown>,
): { x1: number; y1: number; x2: number; y2: number } | null {
  const sp = p.StartPoint;
  const ep = p.EndPoint;
  const startObj = sp != null && typeof sp === "object" && !Array.isArray(sp) ? (sp as Record<string, unknown>) : null;
  const endObj = ep != null && typeof ep === "object" && !Array.isArray(ep) ? (ep as Record<string, unknown>) : null;
  const p1 = readPoint(startObj);
  const p2 = readPoint(endObj);

  const x1 = parseNumber(p.X1) ?? parseNumber(p.x1) ?? p1.x;
  const y1 = parseNumber(p.Y1) ?? parseNumber(p.y1) ?? p1.y;
  const x2 = parseNumber(p.X2) ?? parseNumber(p.x2) ?? p2.x;
  const y2 = parseNumber(p.Y2) ?? parseNumber(p.y2) ?? p2.y;

  if (x1 !== null && y1 !== null && x2 !== null && y2 !== null) {
    return { x1, y1, x2, y2 };
  }
  return null;
}

function adjustExplicitIfAbsolute(
  ep: { x1: number; y1: number; x2: number; y2: number },
  left: number,
  top: number,
  w: number,
  h: number,
): { x1: number; y1: number; x2: number; y2: number } {
  const tol = 0.5;
  const maxLocal = Math.max(w, h) + tol;
  const looksAbsolute =
    ep.x1 > maxLocal || ep.x2 > maxLocal || ep.y1 > maxLocal || ep.y2 > maxLocal || ep.x1 < -tol || ep.y1 < -tol;
  if (!looksAbsolute) return ep;
  return {
    x1: ep.x1 - left,
    y1: ep.y1 - top,
    x2: ep.x2 - left,
    y2: ep.y2 - top,
  };
}

function endpointsFromDirectionEnum(
  dir: number,
  w: number,
  h: number,
): { x1: number; y1: number; x2: number; y2: number } {
  const d = Math.floor(dir);
  if (d === 4) {
    return { x1: w, y1: 0, x2: 0, y2: h };
  }
  const d4 = ((d % 4) + 4) % 4;
  switch (d4) {
    case 0:
      return { x1: 0, y1: h / 2, x2: w, y2: h / 2 };
    case 1:
      return { x1: w / 2, y1: 0, x2: w / 2, y2: h };
    case 2:
      return { x1: 0, y1: 0, x2: w, y2: h };
    case 3:
      return { x1: 0, y1: h, x2: w, y2: 0 };
    default:
      return { x1: 0, y1: 0, x2: w, y2: h };
  }
}

function normalizeDirectionEnum(raw: number, opts?: { fromRawUInt32: boolean }): number {
  const v = Math.floor(raw);
  if (opts?.fromRawUInt32) {
    if (v >= 0 && v <= 4) return v;
    return ((v % 4) + 4) % 4;
  }
  if (v === 0) return 0;
  if (v >= 1 && v <= 4) return v - 1;
  return ((v % 4) + 4) % 4;
}

function parseCLineSettingsFromInts(ints: number[]): { direction: number } | null {
  if (ints.length < 11) return null;
  const fromLeftTop = ints[2] !== 0;
  return { direction: fromLeftTop ? 2 : 3 };
}

function pickPenWidthSlotFromInts(ints: number[]): { slot: number; width: number } | null {
  for (let i = 1; i < Math.min(ints.length, 12); i++) {
    const v = ints[i];
    if (typeof v === "number" && v >= 1 && v <= 32) return { slot: i, width: v };
  }
  return null;
}

function pickDirectionFromUInt32ArrayLegacy(ints: number[]): number | null {
  const pw = pickPenWidthSlotFromInts(ints);
  const widthSlot = pw?.slot ?? -1;
  const skipAdjacentDuplicateDir =
    widthSlot >= 0 &&
    widthSlot + 1 < ints.length &&
    typeof ints[widthSlot] === "number" &&
    typeof ints[widthSlot + 1] === "number" &&
    ints[widthSlot] <= 3 &&
    ints[widthSlot + 1] === ints[widthSlot] &&
    ints[widthSlot + 1] <= 3;

  const prioritySlots = [4, 5, 6, 3, 7, 2, 8, 1, 0, 9, 10, 11];
  for (const i of prioritySlots) {
    if (i >= ints.length) continue;
    if (i === widthSlot) continue;
    if (skipAdjacentDuplicateDir && i === widthSlot + 1) continue;
    const v = ints[i];
    if (typeof v !== "number" || v < 0 || v > 3) continue;
    return v;
  }
  return null;
}

function pickDirectionFromUInt32Array(ints: number[]): number | null {
  const cline = parseCLineSettingsFromInts(ints);
  if (cline) return cline.direction;
  return pickDirectionFromUInt32ArrayLegacy(ints);
}

function directionFromThinBbox(w: number, h: number): number | null {
  const rawW = Math.max(1, w);
  const rawH = Math.max(1, h);
  const isHorizontal = rawH <= 3 || rawH <= rawW * 0.12;
  const isVertical = rawW <= 3 || rawW <= rawH * 0.12;
  if (isHorizontal && !isVertical) return 0;
  if (isVertical && !isHorizontal) return 1;
  return null;
}

function parseDirectionFromProps(p: Record<string, unknown>): number | null {
  const fromRawUInt32 = Array.isArray(p.LineSettingsRawUInt32) && (p.LineSettingsRawUInt32 as unknown[]).length > 0;
  if (fromRawUInt32) {
    const picked = pickDirectionFromUInt32Array(p.LineSettingsRawUInt32 as number[]);
    if (picked !== null) return picked;
  }
  const d =
    p.Direction ??
    p.LineDirection ??
    p.Orientation ??
    p.lineDirection ??
    p.LineOrientation ??
    p.DrawDirection ??
    p.LineDrawDirection;

  if (typeof d === "number" && Number.isFinite(d)) return normalizeDirectionEnum(d, { fromRawUInt32 });

  if (typeof d === "string") {
    const s = d.toLowerCase().replace(/\s/g, "");
    if (s === "0" || s.includes("horizontal") || s === "h" || s.includes("ldhorizontal")) return 0;
    if (s === "1" || s.includes("vertical") || s === "v" || s.includes("ldvertical")) return 1;
    if (
      s.includes("diagonal2") ||
      s.includes("ldiagonal2") ||
      s.includes("diagonalup") ||
      s.includes("upward") ||
      s === "/"
    ) {
      return 3;
    }
    if (
      s.includes("diagonal1") ||
      s.includes("ldiagonal") ||
      s.includes("diagonaldown") ||
      s.includes("downward") ||
      s === "\\" ||
      (s.includes("tl") && s.includes("br"))
    ) {
      return 2;
    }
    if ((s.includes("bl") && s.includes("tr")) || s.includes("bottomleft") || s.includes("topright")) return 3;
  }
  return null;
}

function normalizeAngleDegrees(raw: number): number {
  let a = raw;
  if (Math.abs(a) > 360 && Math.abs(a) < 360000) a = a / 10;
  return a;
}

function endpointsFromAngleDeg(
  w: number,
  h: number,
  angleDeg: number,
): { x1: number; y1: number; x2: number; y2: number } {
  const cx = w / 2;
  const cy = h / 2;
  const rad = (angleDeg * Math.PI) / 180;
  const ux = Math.cos(rad);
  const uy = Math.sin(rad);
  const eps = 1e-9;
  const ts: number[] = [];

  const pushIfOnRect = (t: number) => {
    const x = cx + t * ux;
    const y = cy + t * uy;
    if (x >= -eps && x <= w + eps && y >= -eps && y <= h + eps) ts.push(t);
  };

  if (Math.abs(ux) > eps) {
    pushIfOnRect((0 - cx) / ux);
    pushIfOnRect((w - cx) / ux);
  }
  if (Math.abs(uy) > eps) {
    pushIfOnRect((0 - cy) / uy);
    pushIfOnRect((h - cy) / uy);
  }

  if (ts.length < 2) {
    return { x1: 0, y1: 0, x2: w, y2: h };
  }

  const tMin = Math.min(...ts);
  const tMax = Math.max(...ts);
  return {
    x1: cx + tMin * ux,
    y1: cy + tMin * uy,
    x2: cx + tMax * ux,
    y2: cy + tMax * uy,
  };
}

function heuristicEndpoints(w: number, h: number): { x1: number; y1: number; x2: number; y2: number } {
  const rawW = Math.max(1, w);
  const rawH = Math.max(1, h);
  const isHorizontal = rawH <= 3 || rawH <= rawW * 0.15;
  const isVertical = rawW <= 3 || rawW <= rawH * 0.15;
  if (isHorizontal && !isVertical) {
    return { x1: 0, y1: rawH / 2, x2: rawW, y2: rawH / 2 };
  }
  if (isVertical && !isHorizontal) {
    return { x1: rawW / 2, y1: 0, x2: rawW / 2, y2: rawH };
  }
  return { x1: 0, y1: 0, x2: rawW, y2: rawH };
}

function resolveLineEndpoints(
  p: Record<string, unknown>,
  rawW: number,
  rawH: number,
  left: number,
  top: number,
): { x1: number; y1: number; x2: number; y2: number } {
  const w = Math.max(1, rawW);
  const h = Math.max(1, rawH);
  const flat = gatherLineProps(p);

  let explicit = endpointsFromExplicitProps(flat);
  if (explicit) {
    explicit = adjustExplicitIfAbsolute(explicit, left, top, w, h);
    return explicit;
  }

  const rawU32 = flat.LineSettingsRawUInt32;
  if (Array.isArray(rawU32) && (rawU32 as number[]).length >= 11) {
    const thin = directionFromThinBbox(w, h);
    if (thin !== null) {
      return endpointsFromDirectionEnum(thin, w, h);
    }
  }

  const dir = parseDirectionFromProps(flat);
  if (dir !== null) {
    return endpointsFromDirectionEnum(dir, w, h);
  }

  const angleRaw = parseNumber(flat.Angle ?? flat.angle ?? flat.LineAngle ?? flat.lineAngle);
  if (angleRaw !== null) {
    return endpointsFromAngleDeg(w, h, normalizeAngleDegrees(angleRaw));
  }

  const raw = flat.LineSettingsRawUInt32;
  if (Array.isArray(raw) && raw.length > 0) {
    const v = pickDirectionFromUInt32Array(raw as number[]);
    if (v !== null) return endpointsFromDirectionEnum(v, w, h);
  }

  return heuristicEndpoints(w, h);
}

function uint32ToCssHexColor(v: number): string | null {
  if (!Number.isFinite(v) || v < 0) return null;
  const x = v >>> 0;
  if (x === 0) return null;
  if (x > 0 && x <= 16) return null;
  if (x >= 17 && x <= 127) return null;
  const r = x & 0xff;
  const g = (x >> 8) & 0xff;
  const b = (x >> 16) & 0xff;
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function pickColorFromUInt32Slots(ints: number[]): string | null {
  const slots = [1, 2, 3, 5, 6];
  for (const i of slots) {
    if (i >= ints.length) continue;
    const v = ints[i];
    if (typeof v !== "number") continue;
    const hex = uint32ToCssHexColor(v);
    if (hex) return hex;
  }
  return null;
}

function pickStrokeColor(p: Record<string, unknown>): string {
  const flat = gatherLineProps(p);
  const pen = flat.Pen;
  const penObj =
    pen != null && typeof pen === "object" && !Array.isArray(pen) ? (pen as Record<string, unknown>) : null;
  const line = flat.Line;
  const lineObj =
    line != null && typeof line === "object" && !Array.isArray(line) ? (line as Record<string, unknown>) : null;

  const rawU32 = flat.LineSettingsRawUInt32;
  let fromRaw: string | null = null;
  if (Array.isArray(rawU32) && rawU32.length > 0) {
    fromRaw = pickColorFromUInt32Slots(rawU32 as number[]);
  }

  const rawArr = Array.isArray(rawU32) ? (rawU32 as number[]) : [];
  const ignoreFake010000 = rawArr.length > 2 && rawArr[2] === 1;

  const candidates = [
    fromRaw,
    flat.PenColor,
    flat.LineColor,
    flat.Color,
    flat.ForeGroundColor,
    flat.BackGroundColor,
    penObj?.Color,
    lineObj?.Color,
    lineObj?.PenColor,
  ];

  for (const c of candidates) {
    if (typeof c !== "string" || c.trim() === "") continue;
    const n = normalizeHex(c);
    if (ignoreFake010000 && n.toLowerCase() === "#010000") continue;
    return n;
  }
  return DEFAULT_STROKE;
}

function pickStrokeDashArray(p: Record<string, unknown>): string | undefined {
  const flat = gatherLineProps(p);
  const style =
    flat.PenStyle ??
    flat.LineStyle ??
    (typeof flat.Pen === "object" && flat.Pen && !Array.isArray(flat.Pen)
      ? (flat.Pen as Record<string, unknown>).Style
      : undefined);

  if (typeof style === "string") {
    const s = style.toLowerCase();
    if (s.includes("dash")) return "8 5";
    if (s.includes("dot")) return "2 4";
  }
  if (typeof style === "number" && (style === 1 || style === 2 || style === 3)) {
    return "8 5";
  }
  return undefined;
}

function gdiArrowDimensions(side: number, angleDeg: number): { len: number; halfBase: number } {
  const s = Math.max(1, Math.min(256, side));
  const a = Math.max(1, Math.min(179, angleDeg));
  const fArrowW = 2 * s * Math.sin((a * Math.PI) / 360);
  const half = fArrowW / 2;
  const len = Math.sqrt(Math.max(0, s * s - half * half));
  return { len, halfBase: half };
}

function clineSlotInt(
  flat: Record<string, unknown>,
  key: string,
  raw: number[] | undefined,
  idx: number,
  fallback: number,
): number {
  const p = parseNumber(flat[key]);
  if (p !== null && Number.isFinite(p)) return p;
  if (raw && raw.length > idx && typeof raw[idx] === "number") return raw[idx];
  return fallback;
}

function getEndCapState(flat: Record<string, unknown>): {
  leftEnd: number;
  rightEnd: number;
  leftPointR: number;
  rightPointR: number;
  leftArrowAngle: number;
  leftArrowSide: number;
  rightArrowAngle: number;
  rightArrowSide: number;
} {
  const raw = flat.LineSettingsRawUInt32 as number[] | undefined;

  let leftEnd: number | null = null;
  let rightEnd: number | null = null;
  if (Array.isArray(raw)) {
    if (raw.length > 3 && typeof raw[3] === "number") leftEnd = raw[3];
    if (raw.length > 7 && typeof raw[7] === "number") rightEnd = raw[7];
  }
  if (leftEnd == null) leftEnd = parseNumber(flat.LineLeftEnd);
  if (rightEnd == null) rightEnd = parseNumber(flat.LineRightEnd);

  if (leftEnd == null && (flat.ArrowStart === true || flat.LineArrowStart === true)) leftEnd = 2;
  if (leftEnd == null) leftEnd = 0;

  if (rightEnd == null && (flat.ArrowEnd === true || flat.LineArrowEnd === true)) rightEnd = 2;
  if (rightEnd == null) rightEnd = 0;

  if (leftEnd !== 1 && leftEnd !== 2 && (flat.ArrowStart === true || flat.LineArrowStart === true)) leftEnd = 2;
  if (rightEnd !== 1 && rightEnd !== 2 && (flat.ArrowEnd === true || flat.LineArrowEnd === true)) rightEnd = 2;

  return {
    leftEnd: Math.min(2, Math.max(0, Math.floor(leftEnd))),
    rightEnd: Math.min(2, Math.max(0, Math.floor(rightEnd))),
    leftPointR: clineSlotInt(flat, "LineLeftPointRadius", raw, 4, 2),
    leftArrowAngle: clineSlotInt(flat, "LineLeftArrowAngle", raw, 5, 30),
    leftArrowSide: clineSlotInt(flat, "LineLeftArrowSide", raw, 6, 5),
    rightPointR: clineSlotInt(flat, "LineRightPointRadius", raw, 8, 2),
    rightArrowAngle: clineSlotInt(flat, "LineRightArrowAngle", raw, 9, 30),
    rightArrowSide: clineSlotInt(flat, "LineRightArrowSide", raw, 10, 5),
  };
}

/**
 * Размеры стрелки в единицах viewBox (≈ px).
 * lineLen — длина отрезка в viewBox; используем её как ограничитель,
 * чтобы стрелка не была больше половины линии и не вылезала за края.
 */
function pickArrowDims(
  side: number,
  angle: number,
  lineLen: number,
  strokeWidth: number,
): { len: number; halfBase: number } {
  const maxLen = Math.max(8, lineLen * 0.3);
  const penScale = Math.max(1, Math.min(5, strokeWidth / 2));
  let len: number;
  let halfBase: number;
  if (side >= 1 && angle >= 1 && angle <= 179) {
    const d = gdiArrowDimensions(side, angle);
    len = Math.min(d.len * ARROW_GDI_BASE_SCALE * penScale, maxLen);
    halfBase = Math.min(d.halfBase * ARROW_GDI_BASE_SCALE * penScale, maxLen * 0.65);
    len = Math.max(len, strokeWidth * 1.2);
    halfBase = Math.max(halfBase, strokeWidth * 0.85);
  } else {
    len = Math.max(8, Math.min(maxLen, strokeWidth * 4 + 6));
    halfBase = len * 0.55;
  }
  return { len: len * ARROW_GLOBAL_SCALE, halfBase: halfBase * ARROW_GLOBAL_SCALE };
}

export const TiSchemeLine: FC<ITiSchemeLineProps> = ({ control, flashUnacknowledgedAlarms, flashFaultAlarms }) => {
  void flashUnacknowledgedAlarms;
  void flashFaultAlarms;
  const { props } = control;

  const baseStroke = useMemo(() => pickStrokeColor((props || {}) as Record<string, unknown>), [props]);
  const stroke = baseStroke;

  const strokeWidth = useMemo(() => {
    const flat = gatherLineProps((props || {}) as Record<string, unknown>);
    const pen = flat.Pen;
    const penW =
      typeof pen === "object" && pen && !Array.isArray(pen)
        ? parseNumber((pen as Record<string, unknown>).Width)
        : null;
    const w = penW ?? parseNumber(flat.LineWidth) ?? parseNumber(flat.PenWidth) ?? parseNumber(flat.StrokeWidth);
    if (w !== null && w > 0) {
      return Math.min(12, Math.max(1, w));
    }
    return 2;
  }, [props]);

  const strokeDasharray = useMemo(() => pickStrokeDashArray((props || {}) as Record<string, unknown>), [props]);

  const { w, h, line } = useMemo(() => {
    const rawW = Math.max(1, Number(props.Width) || 1);
    const rawH = Math.max(1, Number(props.Height) || 1);
    const left = Number(props.Left) || 0;
    const top = Number(props.Top) || 0;
    const p = (props || {}) as Record<string, unknown>;
    const ep = resolveLineEndpoints(p, rawW, rawH, left, top);
    return { w: rawW, h: rawH, line: ep };
  }, [props]);

  const endCaps = useMemo(() => getEndCapState(gatherLineProps((props || {}) as Record<string, unknown>)), [props]);

  const lineLen = Math.hypot(line.x2 - line.x1, line.y2 - line.y1) || 1;

  const padNeeded = useMemo(() => {
    let m = strokeWidth / 2 + 4;
    if (endCaps.leftEnd === 2) {
      const d = pickArrowDims(endCaps.leftArrowSide, endCaps.leftArrowAngle, lineLen, strokeWidth);
      m = Math.max(m, d.len + 2, d.halfBase + 2);
    }
    if (endCaps.rightEnd === 2) {
      const d = pickArrowDims(endCaps.rightArrowSide, endCaps.rightArrowAngle, lineLen, strokeWidth);
      m = Math.max(m, d.len + 2, d.halfBase + 2);
    }
    if (endCaps.leftEnd === 1) m = Math.max(m, Math.min(80, Math.max(1, endCaps.leftPointR)) + 2);
    if (endCaps.rightEnd === 1) m = Math.max(m, Math.min(80, Math.max(1, endCaps.rightPointR)) + 2);
    return m;
  }, [endCaps, lineLen, strokeWidth]);

  const padY = padNeeded;
  const padX = h > 0 ? (padNeeded * w) / h : padNeeded;
  const vbW = w + padX * 2;
  const vbH = h + padY * 2;

  const arrowPolygons = useMemo(() => {
    const x1 = line.x1 + padX;
    const y1 = line.y1 + padY;
    const x2 = line.x2 + padX;
    const y2 = line.y2 + padY;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dlen = Math.hypot(dx, dy) || 1;
    const ux = dx / dlen;
    const uy = dy / dlen;
    const perp = { x: -uy, y: ux };

    let end: string | null = null;
    let start: string | null = null;
    let lineX1 = x1;
    let lineY1 = y1;
    let lineX2 = x2;
    let lineY2 = y2;

    if (endCaps.rightEnd === 2) {
      const { len, halfBase } = pickArrowDims(endCaps.rightArrowSide, endCaps.rightArrowAngle, lineLen, strokeWidth);
      const b1x = x2 - ux * len + perp.x * halfBase;
      const b1y = y2 - uy * len + perp.y * halfBase;
      const b2x = x2 - ux * len - perp.x * halfBase;
      const b2y = y2 - uy * len - perp.y * halfBase;
      end = `${x2},${y2} ${b1x},${b1y} ${b2x},${b2y}`;
      lineX2 = x2 - ux * len;
      lineY2 = y2 - uy * len;
    }
    if (endCaps.leftEnd === 2) {
      const { len, halfBase } = pickArrowDims(endCaps.leftArrowSide, endCaps.leftArrowAngle, lineLen, strokeWidth);
      const b1x = x1 + ux * len + perp.x * halfBase;
      const b1y = y1 + uy * len + perp.y * halfBase;
      const b2x = x1 + ux * len - perp.x * halfBase;
      const b2y = y1 + uy * len - perp.y * halfBase;
      start = `${x1},${y1} ${b1x},${b1y} ${b2x},${b2y}`;
      lineX1 = x1 + ux * len;
      lineY1 = y1 + uy * len;
    }
    return { start, end, lineX1, lineY1, lineX2, lineY2 };
  }, [endCaps, line, padX, padY, lineLen, strokeWidth]);

  return (
    <Box
      sx={{
        position: "absolute",
        left: props.Left,
        top: props.Top,
        width: props.Width,
        height: props.Height,
        pointerEvents: "none",
        overflow: "visible",
      }}
    >
      <Box
        component="svg"
        viewBox={`0 0 ${vbW} ${vbH}`}
        preserveAspectRatio="xMidYMid meet"
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          overflow: "visible",
          shapeRendering: "geometricPrecision",
        }}
        aria-hidden
      >
        <line
          x1={arrowPolygons.lineX1}
          y1={arrowPolygons.lineY1}
          x2={arrowPolygons.lineX2}
          y2={arrowPolygons.lineY2}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
        />
        {endCaps.leftEnd === 1 && (
          <circle
            cx={line.x1 + padX}
            cy={line.y1 + padY}
            r={Math.min(80, Math.max(1, endCaps.leftPointR))}
            fill={stroke}
            stroke="none"
            pointerEvents="none"
          />
        )}
        {endCaps.rightEnd === 1 && (
          <circle
            cx={line.x2 + padX}
            cy={line.y2 + padY}
            r={Math.min(80, Math.max(1, endCaps.rightPointR))}
            fill={stroke}
            stroke="none"
            pointerEvents="none"
          />
        )}
        {arrowPolygons.start && (
          <polygon points={arrowPolygons.start} fill={stroke} stroke="none" pointerEvents="none" />
        )}
        {arrowPolygons.end && <polygon points={arrowPolygons.end} fill={stroke} stroke="none" pointerEvents="none" />}
      </Box>
    </Box>
  );
};
